/**
 * Route guard for the admin dashboard.
 *
 * Like ProtectedRoute this is presentation only — every admin endpoint checks
 * the role server-side.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider.jsx';
import { PageLoader } from '../feedback/PageLoader.jsx';
import { AccountUnavailable } from '../feedback/AccountUnavailable.jsx';
import { EmptyState } from '../ui/EmptyState.jsx';
import { Button } from '../ui/Button.jsx';
import { config } from '../../config/appConfig.js';

export function AdminRoute({ children }) {
  const { isAuthenticated, initialized, isModerator, profileMissing, syncError } = useAuth();
  const location = useLocation();

  if (!initialized) return <PageLoader />;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  /*
   * Must precede the role check, and not merely for consistency with
   * ProtectedRoute. `isModerator` reads `profile.role`, so a missing profile
   * makes every administrator look like an ordinary user — the screen below
   * would tell them they lack access when the truth is that the server never
   * answered. Wrong diagnosis, and one that sends them hunting through
   * permissions instead of looking at the API.
   */
  if (profileMissing) {
    return syncError ? <AccountUnavailable /> : <PageLoader />;
  }

  if (!isModerator) {
    return (
      <EmptyState
        title="You do not have access to this area"
        description={`The admin dashboard is limited to ${config.appName} moderators and administrators.`}
        action={
          <Button to="/app" variant="primary">
            Back to my library
          </Button>
        }
      />
    );
  }

  return children;
}
